import { getShowStudyPackViews } from "@helpers/exams";

import {
	getNumAttempted,
	getTopicsSectionLabel,
	groupTagsByType,
} from "@helpers/content/tag";
import { buildPremiumTopicGroups } from "../helpers/freemium-home";
import { buildGroupSectionTitle, getStudyPackItemCount } from "../helpers/tag";

import { useTagsStore } from "./useTagsStore";

import { isScreenshotMode } from "@constants/common";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { getColorScaleAndDescription } from "@helpers/content/metadata";
import { useAppStore } from "@hooks/useAppStore";
import { smartPluralize } from "@memorang/helpers/common";
import type { MixedItemDist, MixedItemTag } from "@memorang/types";
import type { TopicsSectionItem } from "@memorang/ui/components/HomeApplet/types";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import { useDevice } from "@memorang/ui/useDevice";
import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";

type TopicGroupSectionData = {
	sectionTitle: string;
	isLoading: boolean;
	isEnabled: boolean;
	data: TopicsSectionItem[];
	type: string;
	isPremium?: true;
	onPress?: () => void;
};

const getTagsToDisplay = (
	tags: MixedItemTag[],
	isMobile?: boolean,
	isDetailedList?: boolean,
) => {
	if (isMobile && !isDetailedList) {
		return [
			...tags.filter((tag) => !tag.locked),
			...tags.filter((tag) => tag.locked),
		];
	}
	return tags.sort((a, b) => Number(a.locked) - Number(b.locked));
};

const getMasteryKeyTopicsByGroup = (
	mixedItemDist: MixedItemDist,
	hasAccess?: boolean,
	isMobile?: boolean,
	isDetailedList?: boolean,
) => {
	const { tags, freeTagIds } = mixedItemDist;
	const groupedTagsByType = groupTagsByType(
		tags,
		"Display",
		freeTagIds,
		hasAccess,
	);
	return Object.entries(groupedTagsByType)
		.filter(([, groupTags]) => groupTags.length > 0)
		.map(([type, groupTags]) => ({
			type,
			tags: getTagsToDisplay(groupTags, isMobile, isDetailedList),
		}));
};

export const useMasteryKeyTopicsSectionData = (
	mixedItemDist?: MixedItemDist,
	hasAccess?: boolean,
	premiumMixedItemDist?: MixedItemDist,
): TopicGroupSectionData[] | null => {
	const router = useRouter();
	const theme = useAppTheme();
	const { t } = useTranslation();
	const showPointsBasedProgress = useAppStore(
		(state) => state.reportView?.formative?.showPointsBasedScore,
	);

	const {
		currentExamContext: { studyPackCollectionId, examId },
	} = useExamContext();

	const setCurrentSelectedMasteryItems = useTagsStore(
		(state) => state.setCurrentSelectedMasteryItems,
	);
	const { isMobile } = useDevice();

	const isStudyPackEnabled = examId
		? getShowStudyPackViews(studyPackCollectionId)
		: true;

	if (!isStudyPackEnabled) {
		return null;
	}

	if (!mixedItemDist) {
		return [
			{
				sectionTitle: getTopicsSectionLabel(undefined),
				isLoading: true,
				isEnabled: true,
				data: [],
				type: "",
			},
		];
	}

	const groups = getMasteryKeyTopicsByGroup(mixedItemDist, hasAccess, isMobile);
	const numItems = getStudyPackItemCount(mixedItemDist.contents);

	const mapTopicsSectionItems = (
		groupTags: MixedItemTag[],
	): TopicsSectionItem[] =>
		groupTags.map((item) => ({
			...item,
			locked: isStudyPackEnabled ? (item.locked ?? false) : false,
			score: showPointsBasedProgress
				? getNumAttempted(item.distribution)
				: isScreenshotMode
					? Math.floor(Math.random() * (item.numItems - 8)) + 9
					: (item.numCorrect ?? 0),
			rightText: t("progress.tagProgress.itemCount", {
				count: item.numItems || 0,
				item: smartPluralize("item", item.numItems || 0),
			}),
			...getColorScaleAndDescription({
				theme,
				tag: item,
				t,
				includeColorScale: true,
			}),
		}));

	const freeSections: TopicGroupSectionData[] = groups.map(
		({ type, tags: groupTags }) => {
			const mappedItems = mapTopicsSectionItems(groupTags);

			return {
				sectionTitle: buildGroupSectionTitle(type, groupTags, numItems),
				isLoading: false,
				isEnabled: mappedItems.length > 0,
				data: mappedItems,
				type,
				onPress: () => {
					const detailedGroups = getMasteryKeyTopicsByGroup(
						mixedItemDist,
						hasAccess,
						isMobile,
						true,
					);
					const detailedGroup = detailedGroups.find((g) => g.type === type);
					setCurrentSelectedMasteryItems(detailedGroup?.tags ?? []);
					router.push({
						pathname: "/progress/detailed-list/mastery",
						params: { type },
					});
				},
			};
		},
	);

	const premiumGroups = buildPremiumTopicGroups(premiumMixedItemDist);
	const premiumNumItems = getStudyPackItemCount(premiumMixedItemDist?.contents);
	const premiumSections: TopicGroupSectionData[] = premiumGroups.map(
		({ type, tags: groupTags }) => ({
			sectionTitle: buildGroupSectionTitle(type, groupTags, premiumNumItems),
			isLoading: false,
			isEnabled: groupTags.length > 0,
			data: mapTopicsSectionItems(groupTags),
			type,
			isPremium: true,
		}),
	);

	return [...freeSections, ...premiumSections];
};
